import React, { FC, ReactElement } from 'react';
import { Box, Chip, type SxProps, type Theme, useTheme } from '@mui/material';
import { mergeSx } from 'merge-sx';

import StandardPopper from '@/components/StandardPopper/StandardPopper';
import { borders } from '@/theme/constants';
import typography from '@/theme/typography';

interface HiddenItemCounterProps {
  children?: ReactElement | false | null;
  hiddenItemsCount: number;
  isDisabled?: boolean;
  sx?: SxProps<Theme>;
}

const HiddenItemCounter: FC<HiddenItemCounterProps> = ({ children, hiddenItemsCount, isDisabled, sx }) => {
  const { palette, spacing } = useTheme();

  return (
    <Box
      sx={mergeSx(
        {
          paddingInlineStart: 1,
          paddingInlineEnd: 0,
          display: 'flex',
          alignItems: 'center'
        },
        sx
      )}
    >
      {!!hiddenItemsCount && (
        <StandardPopper
          noTrigger={!children}
          content={children || <></>}
          caretStartPosition="50%"
          getTriggerSx={isOpen =>
            mergeSx<Theme>(
              isOpen && {
                '& .MuiChip-root': {
                  border: borders.getSolid(palette.primary.main, borders._1_5px)
                },
                '& .MuiChip-label': {
                  color: 'primary.900'
                }
              }
            )
          }
          getSx={() =>
            mergeSx<Theme>(
              isDisabled && {
                filter: 'grayscale(1)'
              }
            )
          }
        >
          <Chip
            label={`+${hiddenItemsCount}`}
            sx={mergeSx<Theme>(
              {
                bgcolor: palette.background.default,
                border: borders.getSolid(palette.primary[300]!, borders._thin),
                width: spacing(3),
                height: spacing(2.5),
                p: 0,
                '& .MuiChip-label': {
                  ...typography.body3,
                  p: 0,
                  m: spacing(0.5, 'auto'),
                  color: 'grey.800',
                  textOverflow: 'clip'
                }
              },
              !!children && {
                '&:hover': {
                  bgcolor: 'primary.200',
                  borderColor: 'primary.main'
                },
                '&:hover .MuiChip-label': {
                  color: 'primary.900'
                }
              }
            )}
          />
        </StandardPopper>
      )}
    </Box>
  );
};

export default HiddenItemCounter;
